import React from 'react';
import { observer } from 'mobx-react-lite';
import Grid from '@material-ui/core/Grid';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import { useRootStore } from '../../../../mobx/bridge';
import styles from './EditCannedMessages.module.css';
import * as utils from '../../../../helpers/utils';
import CannedMessageListItem from '../CannedMessageListItem/CannedMessageListItem';

// eslint-disable-next-line prefer-arrow-callback,sonarjs/cognitive-complexity
const EditCannedMessages = observer(function EditCannedMessages() {
  const {
    commonStore,
    cannedMessageStore,
    settingsStore,
    sendingEvents,
    cannedMessageTypeStore,
  } = useRootStore();
  const { t } = useTranslation();

  const [messageInput, setMessageInput] = React.useState('');
  const [newCursorPosition, setNewCursorPosition] = React.useState();
  const [editItemActive, setEditItemActive] = React.useState(false);
  const cannedMessageInputRef = React.useRef();

  React.useEffect(() => {
    if (editItemActive && cannedMessageInputRef?.current) {
      if (newCursorPosition) {
        cannedMessageInputRef.current.selectionStart = newCursorPosition;
        cannedMessageInputRef.current.selectionEnd = newCursorPosition;
      }
      cannedMessageInputRef.current.focus();
    }
  }, [editItemActive, newCursorPosition]);

  const settings = settingsStore.getLoggedUserSettings;

  const cannedMessages = cannedMessageStore.computedGetCannedMessagesForChannelType(
    settings?.SelectedChannel?.ChannelType?.TypeId
  );

  function closeComponent() {
    commonStore.setMainComponentOpen(null);
  }

  function onMessageChange(e) {
    setMessageInput(e.target.value);
  }

  const toggleEditItem = () => {
    setEditItemActive(!editItemActive);
    setMessageInput('');
  };

  const submitCannedMessage = debounce(() => {
    if (!messageInput || !messageInput.trim()) {
      return;
    }

    let cannedMessageType;
    if (settings?.SelectedChannel?.ChannelType?.TypeId === utils.ChannelTypeEnum.SMS.TypeId) {
      cannedMessageType = cannedMessageTypeStore.computedGetByTypeId(
        utils.CannedMessageTypeEnum.SMS.TypeId
      );
    } else if (
      settings?.SelectedChannel?.ChannelType?.TypeId === utils.ChannelTypeEnum.WebChat.TypeId
    ) {
      cannedMessageType = cannedMessageTypeStore.computedGetByTypeId(
        utils.CannedMessageTypeEnum.WebChat.TypeId
      );
    }

    const cannedMessage = {
      _id: utils.generateGuid(),
      Text: messageInput,
      CannedMessageType: cannedMessageType,
    };

    sendingEvents.socketEmitUpsertCannedMessage(cannedMessage);
    toggleEditItem();
  }, 250);

  const handleOnKeyDown = (e) => {
    const keyCode = e.keyCode || e.which;
    if (keyCode === 13) {
      // 13 is the enter keycode
      cannedMessageInputRef.current.removeEventListener('blur', submitCannedMessage);
      submitCannedMessage();
      cannedMessageInputRef.current.blur();
    }
  };

  const focusInput = () => {
    if (!editItemActive) {
      toggleEditItem();
    }
  };

  const appendKeywordToInput = (e) => {
    if (cannedMessageInputRef?.current) {
      const cursorPosition = cannedMessageInputRef.current.selectionStart;
      const textBeforeCursorPosition = cannedMessageInputRef.current.value.substring(
        0,
        cursorPosition
      );
      const textAfterCursorPosition = cannedMessageInputRef.current.value.substring(
        cursorPosition,
        cannedMessageInputRef.current.value.length
      );
      const cannedMessageKeyword = e.target.value;
      const allText = textBeforeCursorPosition + cannedMessageKeyword + textAfterCursorPosition;

      setMessageInput(allText);
      setNewCursorPosition(cursorPosition + cannedMessageKeyword.length);
    }
  };

  return (
    <Grid component="div" container className={styles.cannedMessagesHolder}>
      <Grid component="div" container item xs={12}>
        <span className={styles.createChannelHeader}>{t('Common.CannedMessages')}</span>

        <button type="button" className={styles.closeBtn} onClick={closeComponent} />
      </Grid>

      <Grid component="div" item xs={12}>
        <span className={styles.cannedMessageSubTitle}>{t('EditCannedMessages.Messages')}</span>

        <span className={styles.separator} />

        <Grid component="div" item xs={12} className={styles.inputHolder}>
          <input
            type="search"
            placeholder={t('EditCannedMessages.AddCannedMessage')}
            value={messageInput}
            onChange={onMessageChange}
            onFocus={focusInput}
            onKeyDown={handleOnKeyDown}
            ref={cannedMessageInputRef}
            data-testid="messageInput"
            maxLength="1600"
          />

          {editItemActive ? (
            <Grid
              component="div"
              item
              xs={12}
              className={styles.actionBtnsContainer}
              data-testid="editMenu"
            >
              {commonStore?.cannedMessagesKeywords ? (
                <div className={styles.cannedMessagesKeywordsContainer}>
                  {Object.entries(commonStore.cannedMessagesKeywords).map((entry) => {
                    const [keyword, value] = entry;
                    return (
                      <button
                        key={keyword}
                        type="button"
                        value={keyword}
                        className={styles.cannedMessagesKeywordsBtn}
                        onClick={appendKeywordToInput}
                      >
                        {value}
                      </button>
                    );
                  })}
                </div>
              ) : null}
              <button
                type="button"
                onClick={submitCannedMessage}
                className={[styles.btn, styles.submitBlueBtn].join(' ')}
                data-testid="saveBtn"
              >
                {t('Common.Save')}
              </button>

              <button
                type="button"
                onClick={toggleEditItem}
                className={[styles.btn, styles.deleteBtn].join(' ')}
                data-testid="cancelBtn"
              >
                {t('Common.Cancel')}
              </button>
            </Grid>
          ) : null}
        </Grid>
      </Grid>

      <Grid component="div" container>
        <Grid
          component="div"
          item
          xs={12}
          className={
            editItemActive
              ? [styles.messageItems, styles.messageItemsReducedHeight].join(' ')
              : styles.messageItems
          }
        >
          {cannedMessages.map((message) => (
            <CannedMessageListItem key={message._id} message={message} />
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
});
EditCannedMessages.whyDidYouRender = true;
EditCannedMessages.displayName = 'EditCannedMessages';

export default EditCannedMessages;
